import { ScaleOrdinal, ScaleQuantize, ScaleThreshold } from 'd3-scale';

import { MinMax } from '../../core/types/min-max.js';
import { DEFAULT_HONEYCOMB_TILE_COLOR } from '../constants.js';

interface GetBaseColorProps {
  scale:
    | ScaleQuantize<string, never>
    | ScaleThreshold<number, string, never>
    | ScaleOrdinal<string, string, string>;
  value: string | number;
}

const isNumeric = (values: GetBaseColorProps): values is NumericTileColorData =>
  typeof values.value === 'number';
const isCategorical = (
  values: GetBaseColorProps,
): values is CategoricalTileColorData => typeof values.value === 'string';

interface NumericTileColorData {
  scale: ScaleQuantize<string> | ScaleThreshold<number, string>;
  value: number;
}

interface CategoricalTileColorData {
  scale: ScaleOrdinal<string, string, string>;
  value: string;
}

const getMinAndMax = (
  scale: ScaleQuantize<string> | ScaleThreshold<number, string>,
  extent: Partial<MinMax> = { min: undefined, max: undefined },
) => {
  const wholeDomain = scale.domain();
  const domainMin = wholeDomain[0];
  const domainMax = wholeDomain[wholeDomain.length - 1];
  const { min: minByConfig, max: maxByConfig } = extent;

  const min = minByConfig ?? domainMin;
  const max = maxByConfig ?? domainMax;
  return { min, max };
};

export function getBaseColor(
  params: GetBaseColorProps,
  extent?: Partial<MinMax>,
) {
  if (isNumeric(params)) {
    const { scale, value } = params;
    const { min, max } = getMinAndMax(scale, extent);
    const isOutOfBounds = value < min || value > max;

    return isOutOfBounds ? DEFAULT_HONEYCOMB_TILE_COLOR : scale(value);
  } else if (isCategorical(params)) {
    const { scale, value } = params;

    return scale(value);
  }
}
